import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";
import { useLeagueSeasonStore } from "@/state/useLeagueSeasonStore";

/** Count of stadium audits submitted by officials and awaiting the league's validation,
 *  across the manager's leagues for the current season. Drives the /missions nav badge so
 *  a planning manager / president sees pending reviews without opening the planner.
 *  Enable only for users who can validate (caller gates on permission). */
export function useAuditReviewBadge(leagueIds: string[], enabled: boolean) {
  const season = useLeagueSeasonStore((s) => s.currentSeason);
  return useQuery({
    queryKey: ["auditReviewBadge", season, leagueIds],
    enabled: enabled && !!season && leagueIds.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<{ count: number }> => {
      // Season-scoped via the missions embed so the count matches the planner's review
      // queue (which is scoped to the selected season).
      const { count, error } = await supabase
        .from("mission_audit_records")
        .select("id, missions!inner(season)", { count: "exact", head: true })
        .eq("target_type", "stadium")
        .eq("status", "submitted")
        .in("league_id", leagueIds)
        .eq("missions.season", season as string);
      if (error) throw new Error(`useAuditReviewBadge: ${error.message}`);
      return { count: count ?? 0 };
    },
  });
}
